import { useRef, useState, type FormEvent } from "react";
import { getOrCreateAuthSession } from "../../api/auth-client";
import { sendAiChat } from "../../api/ai-client";
import type { OmnisyniaClientModule } from "../module.types";
import {
  RAE_DICTIONARY_1884 as dictionary, WIKIPEDIA_ES_URL,
  HISTORICAL_SEARCH_OPTIONS, HISTORICAL_FAVORITES_STORAGE_KEY,
  RAE_1884_REFERENCE, historicalSourceUrl, modernDleUrl,
  normalizeHistoricalTerm, readHistoricalFavorites, addHistoricalFavorite,
  type HistoricalSearchMode
} from "./historical-dictionaries";
import "./omni-wiki.css";

type Section = "diccionarios" | "enciclopedia";

function initialFavorites(): string[] {
  try {
    return readHistoricalFavorites(window.localStorage.getItem(HISTORICAL_FAVORITES_STORAGE_KEY));
  } catch { return []; }
}

function OmniWiki() {
  const [section, setSection] = useState<Section>("diccionarios");
  const [term, setTerm] = useState("");
  const [historicalMode, setHistoricalMode] = useState<HistoricalSearchMode>("lemas");
  const [notice, setNotice] = useState("");
  const [favorites, setFavorites] = useState<string[]>(initialFavorites);
  const [wikiQuery, setWikiQuery] = useState("");
  const [wikiAnswer, setWikiAnswer] = useState("");
  const [wikiError, setWikiError] = useState("");
  const [wikiBusy, setWikiBusy] = useState(false);
  const wikiRequest = useRef(0);

  const cleanTerm = normalizeHistoricalTerm(term);
  const cleanWikiQuery = normalizeHistoricalTerm(wikiQuery);
  const saved = Boolean(cleanTerm && favorites.some(word =>
    word.toLocaleLowerCase("es") === cleanTerm.toLocaleLowerCase("es")));
  const currentSearch = HISTORICAL_SEARCH_OPTIONS.find(item => item.id === historicalMode)
    ?? HISTORICAL_SEARCH_OPTIONS[0];

  function saveFavorites(next: string[]) {
    setFavorites(next);
    try {
      window.localStorage.setItem(HISTORICAL_FAVORITES_STORAGE_KEY, JSON.stringify(next));
    } catch {
      setNotice("Marcadores disponibles en esta sesión; el navegador no permite guardarlos permanentemente.");
    }
  }
  function toggleFavorite() {
    if (!cleanTerm) return;
    if (saved) {
      saveFavorites(favorites.filter(item => item.toLocaleLowerCase("es") !== cleanTerm.toLocaleLowerCase("es")));
      setNotice("Palabra retirada de tus marcadores.");
    } else {
      saveFavorites(addHistoricalFavorite(favorites, cleanTerm));
      setNotice("Palabra guardada en este dispositivo, sin subirla a servidores.");
    }
  }
  async function copyText(text: string, successMessage: string) {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
      await navigator.clipboard.writeText(text);
      setNotice(successMessage);
    } catch {
      setNotice("No se pudo copiar automáticamente. Selecciona y copia el texto manualmente.");
    }
  }
  async function searchWikipedia(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (wikiBusy || !cleanWikiQuery) {
      if (!cleanWikiQuery) setWikiError("Introduce una consulta de hasta 100 caracteres.");
      return;
    }
    const requestId = ++wikiRequest.current;
    setWikiBusy(true);
    setWikiError("");
    setWikiAnswer("");
    try {
      // Reuse the real ONE CORE connector: HP authenticated bridge or Wikimedia REST.
      // Never label a failed or guessed result as Wikipedia.
      const session = await getOrCreateAuthSession();
      const response = await sendAiChat({
        moduleId: "omni-wiki",
        sessionId: session.sessionId,
        sessionToken: session.sessionToken,
        messages: [{ role: "user", content: "/wiki " + cleanWikiQuery }],
        metadata: { locale: "es", platform: "web" },
        userConsent: {
          privacyPolicyAccepted: true, termsAccepted: true,
          aiDisclosureAccepted: true, safetyPolicyAccepted: true, memoryOptIn: false
        }
      });
      if (requestId === wikiRequest.current) setWikiAnswer(response.message.content);
    } catch (error) {
      if (requestId === wikiRequest.current) setWikiError(
        error instanceof Error ? error.message : "ONE CORE no ha podido consultar Wikipedia."
      );
    } finally {
      if (requestId === wikiRequest.current) setWikiBusy(false);
    }
  }

  return (
    <section className="omni-wiki" aria-label="OMNI WIKI">
      <header className="omni-wiki-header">
        <img src="/omnisynia-logo-official-web-v2.png" alt="Logo de OMNISYNIA" className="omni-wiki-logo" />
        <div>
          <p className="omni-wiki-eyebrow">ONE INTELLIGENCE</p>
          <h2>OMNI WIKI</h2>
          <p className="omni-wiki-subtitle">Enciclopedia y biblioteca de referencia</p>
        </div>
      </header>

      <nav className="omni-wiki-tabs" aria-label="Colecciones de OMNI WIKI">
        <button type="button" aria-pressed={section === "diccionarios"}
          className={section === "diccionarios" ? "selected" : ""}
          onClick={() => { setSection("diccionarios"); setNotice(""); }}>
          Diccionarios históricos
        </button>
        <button type="button" aria-pressed={section === "enciclopedia"}
          className={section === "enciclopedia" ? "selected" : ""}
          onClick={() => { setSection("enciclopedia"); setWikiError(""); }}>
          Enciclopedia
        </button>
      </nav>

      {section === "diccionarios" ? (
        <article className="omni-wiki-entry">
          <div className="omni-wiki-book" aria-hidden="true">
            <span>RAE</span><strong>1884</strong><small>XII EDICIÓN</small>
          </div>
          <div className="omni-wiki-entry-body">
            <p className="omni-wiki-eyebrow">FONDO HISTÓRICO · ESPAÑOL</p>
            <h3>{dictionary.title}</h3>
            <p className="omni-wiki-author">{dictionary.author} · {dictionary.edition} · {dictionary.year}</p>
            <p>Diccionario académico histórico. Consulta las acepciones de 1884 sin confundirlas con el diccionario vigente.</p>
            <p className="omni-wiki-metadata">Edición impresa: {dictionary.printPages.toLocaleString("es-ES")} páginas.</p>

            <section className="omni-wiki-research" aria-label="Buscador histórico de la edición de 1884">
              <h4>Búsqueda histórica guiada</h4>
              <label htmlFor="omni-wiki-term">Palabra o expresión</label>
              <input id="omni-wiki-term" type="search" maxLength={100} value={term}
                autoComplete="off" placeholder="Ejemplo: alboroto"
                onChange={event => { setTerm(event.target.value); setNotice(""); }} />
              <p className="omni-wiki-hint">Selecciona el tipo de consulta. Los resultados se consultan en e-DRAE 1884; OMNISYNIA no inventa definiciones.</p>
              <div className="omni-wiki-search-modes" role="group" aria-label="Modalidad de búsqueda">
                {HISTORICAL_SEARCH_OPTIONS.map(option => (
                  <button key={option.id} type="button" aria-pressed={historicalMode === option.id}
                    className={historicalMode === option.id ? "selected" : ""}
                    onClick={() => { setHistoricalMode(option.id); setNotice(""); }}>{option.title}</button>
                ))}
              </div>
              <p className="omni-wiki-mode-description">{currentSearch.description}</p>
              <div className="omni-wiki-actions">
                <a href={historicalSourceUrl(historicalMode)} target="_blank" rel="noopener noreferrer">
                  Abrir búsqueda de {currentSearch.title.toLocaleLowerCase("es")} ↗
                </a>
                <button type="button" disabled={!cleanTerm}
                  onClick={() => void copyText(cleanTerm, "Término copiado. Pégalo en el buscador e-DRAE que abras.")}>
                  Copiar término
                </button>
                <a href={modernDleUrl(cleanTerm)} target="_blank" rel="noopener noreferrer">
                  Comparar en DLE actual ↗
                </a>
              </div>
              <p className="omni-wiki-hint">La palabra no se envía automáticamente al portal externo: cópiala y pégala allí. La comparación moderna abre una obra distinta.</p>
              <div className="omni-wiki-actions omni-wiki-secondary-actions">
                <button type="button" disabled={!cleanTerm} aria-pressed={saved} onClick={toggleFavorite}>
                  {saved ? "★ Quitar marcador" : "☆ Guardar palabra"}
                </button>
                <button type="button" onClick={() => void copyText(RAE_1884_REFERENCE, "Referencia bibliográfica copiada.")}>
                  Copiar referencia bibliográfica
                </button>
              </div>
              {notice && <p role="status" className="omni-wiki-feedback">{notice}</p>}
              {favorites.length > 0 && (
                <div className="omni-wiki-favorites" aria-label="Palabras guardadas en este dispositivo">
                  <h5>Mis palabras guardadas ({favorites.length})</h5>
                  <div className="omni-wiki-favorite-list">
                    {favorites.map(word => <button type="button" key={word}
                      onClick={() => { setTerm(word); setNotice("Marcador recuperado: " + word); }}>{word}</button>)}
                  </div>
                  <p className="omni-wiki-hint">Los marcadores permanecen únicamente en este navegador. No son definiciones verificadas.</p>
                </div>
              )}
            </section>

            <div className="omni-wiki-actions">
              <a href={dictionary.publicFacsimile.url} target="_blank" rel="noopener noreferrer">
                Ver facsímil público (PDF) ↗
              </a>
            </div>
            <p className="omni-wiki-provenance">
              Documento aportado: <strong>{dictionary.uploadedScan.filename}</strong>,
              {" "}{dictionary.uploadedScan.pages.toLocaleString("es-ES")} páginas escaneadas.
              No está alojado en la app. El PDF público enlazado tiene
              {" "}{dictionary.publicFacsimile.pages.toLocaleString("es-ES")} páginas y es una digitalización alternativa.
              Guardarlo en el HP no lo publica automáticamente en la web.
            </p>
          </div>
        </article>
      ) : (
        <article className="omni-wiki-entry omni-wiki-encyclopedia">
          <div className="omni-wiki-entry-body">
            <p className="omni-wiki-eyebrow">FUENTE ENCICLOPÉDICA</p>
            <h3>Wikipedia en español · ONE CORE</h3>
            <p>Consulta artículos verificables desde OMNI WIKI. ONE CORE indicará si la fuente procede de Wikipedia en línea o de una copia offline accesible del HP.</p>
            <form className="omni-wiki-research" onSubmit={event => void searchWikipedia(event)}
              role="search" aria-label="Consultar Wikipedia mediante ONE CORE">
              <label htmlFor="omni-wiki-wikipedia-query">¿Qué quieres consultar?</label>
              <input id="omni-wiki-wikipedia-query" type="search" required maxLength={100}
                value={wikiQuery} placeholder="Ejemplo: sistema solar"
                onChange={event => { setWikiQuery(event.target.value); setWikiError(""); }} />
              <div className="omni-wiki-actions">
                <button type="submit" disabled={wikiBusy || !cleanWikiQuery}>
                  {wikiBusy ? "Consultando ONE CORE…" : "Buscar en OMNI WIKI"}
                </button>
                <a href={WIKIPEDIA_ES_URL} target="_blank" rel="noopener noreferrer">Abrir Wikipedia ↗</a>
              </div>
              {wikiError && <p role="alert" className="omni-wiki-feedback">{wikiError}</p>}
            </form>
            {wikiAnswer && (
              <div className="omni-wiki-wiki-result" aria-label="Respuesta de ONE CORE">
                <h4>Respuesta con procedencia</h4>
                <pre>{wikiAnswer}</pre>
              </div>
            )}
            <p className="omni-wiki-hint">Si no hay fuentes accesibles, se indicará el fallo. Consultar Wikipedia no garantiza conexión offline desde el móvil.</p>
          </div>
        </article>
      )}
      <p className="omni-wiki-footer">Fuentes históricas identificadas por edición y procedencia. Los enlaces externos pueden requerir conexión a Internet.</p>
    </section>
  );
}

export const omniWikiClientModule: OmnisyniaClientModule = {
  id: "omni-wiki",
  displayName: "OMNI WIKI",
  description: "Wikipedia y diccionarios históricos verificados",
  tier: "core",
  status: "available",
  Component: OmniWiki
};
